"use client";

import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import type React from "react";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Chip,
  Box,
  Container,
  Grid,
  Avatar,
  Stack,
  Pagination,
  Skeleton,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import { useParams } from "react-router";
import {
  People as PeopleIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Link } from "react-router-dom";
import { arrowBack } from "ionicons/icons";
import { requestID, baseUrl, config } from "../../common/utils";

const EnrollCourse: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({
    loading: false,
    error: false,
  });
  const [courseList, setCourselist] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(6);

  // Payment Dialog States
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [paymentError, setPaymentError] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    getAllCourses();
  }, []);

  async function getAllCourses() {
    setIsLoading(true);
    setStatus({ loading: true, error: false });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "courses/getAllCourses",
        formData,
        config
      );
      console.log("course", response.data.data);
      setCourselist(response.data.data);
    } catch (error) {
      console.error("API call failed:", error);
      setStatus({ loading: false, error: true });
    } finally {
      setIsLoading(false);
      setStatus({ loading: false, error: false });
    }
  }

  // Payment Functions
  const handleEnrollClick = (course: any) => {
    setSelectedCourse(course);

    if (course.paymenttype?.toLowerCase() === 'paid') {
      setPaymentDialogOpen(true);
      setPaymentStatus('idle');
      setPhoneNumber('');
      setPaymentError('');
    } else {
      // For free courses, navigate directly
      window.location.href = `/folder/viewEnrolledCourse/?id=${course.cosID}`;
    }
  };

  const initiatePayment = async () => {
    if (!phoneNumber.trim()) {
      setPaymentError('Please enter your phone number');
      return;
    }

    // Validate phone number format (Nigeria specific)
    const phoneRegex = /^(\+234|0)[789][01]\d{8}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setPaymentError('Please enter a valid Nigerian phone number');
      return;
    }

    setPaymentStatus('processing');
    setPaymentError('');

    try {
      // Step 1: Generate payment reference
      const paymentRef = `CRS${Date.now()}${Math.random().toString(36).substr(2, 9)}`;

      // Step 2: Prepare payment data for Nomaba
      const paymentData = {
        amount: selectedCourse.amount,
        phone: phoneNumber.replace(/^0/, '+234'), // Format for Nomaba
        reference: paymentRef,
        currency: 'NGN',
        description: `Payment for course: ${selectedCourse.name}`,
        metadata: {
          courseId: selectedCourse.cosID,
          courseName: selectedCourse.name,
          userId: "current-user-id", // Replace with actual user ID from auth context
        }
      };

      // Step 3: Call Nomaba payment API
      // Note: Replace with actual Nomaba API endpoint and credentials
      const nomabaResponse = await initiateNomabaPayment(paymentData);

      if (nomabaResponse.success) {
        // Step 4: Verify payment status
        const verification = await verifyPaymentStatus(paymentRef);

        if (verification.success) {
          setPaymentStatus('success');

          // Step 5: Enroll user in course after successful payment
          await enrollUserInCourse(selectedCourse.cosID);
          // selectedCourse.paymentType = "free"

          // Auto-close dialog and redirect after 2 seconds
          setTimeout(() => {
            setPaymentDialogOpen(false);
            window.location.href = `/folder/viewEnrolledCourse/?id=${selectedCourse.cosID}`;
          }, 2000);
        } else {
          setPaymentStatus('failed');
          setPaymentError(verification.message || 'Payment verification failed');
        }
      } else {
        setPaymentStatus('failed');
        setPaymentError(nomabaResponse.message || 'Payment initiation failed');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      setPaymentError(error.message || 'An error occurred during payment');
    }
  };

  const initiateNomabaPayment = async (paymentData: any) => {
    // Replace with actual Nomaba API call
    // This is a mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate successful payment initiation
        resolve({
          success: true,
          transactionId: `TXN${Date.now()}`,
          message: 'Payment initiated successfully'
        });
      }, 1500);
    });
  };

  const verifyPaymentStatus = async (reference: string) => {
    // Replace with actual payment verification API call
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate successful payment verification
        resolve({
          success: true,
          status: 'completed',
          message: 'Payment completed successfully'
        });
      }, 2000);
    });
  };

  const enrollUserInCourse = async (courseId: string) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("courseId", courseId);
      formData.append("userId", "current-user-id"); // Replace with actual user ID

      const response = await axios.post(
        baseUrl() + "courses/enroll",
        formData,
        config
      );

      return response.data;
    } catch (error) {
      console.error('Enrollment error:', error);
      throw error;
    }
  };

  const handleClosePaymentDialog = () => {
    if (paymentStatus === 'processing') {
      // Don't allow closing while processing
      return;
    }
    setPaymentDialogOpen(false);
    setSelectedCourse(null);
    setPaymentStatus('idle');
    setPaymentError('');
    setPhoneNumber('');
  };

  // Get current courses for pagination
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = courseList.slice(
    indexOfFirstCourse,
    indexOfLastCourse
  );
  const totalPages = Math.ceil(courseList.length / coursesPerPage);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Skeleton loader component
  const CourseSkeleton = () => (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      }}
    >
      <Skeleton variant="rectangular" height={200} />
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width="60%" />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="40%" />
        </Stack>
        <Skeleton variant="text" height={30} width="30%" sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={45} sx={{ borderRadius: 2 }} />
      </CardContent>
    </Card>
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          style={{
            "--background": "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              icon={arrowBack}
              text="Go Back"
              className="back-button"
              style={{ fontWeight: 600, color: "white" }}
            />
          </IonButtons>

          <IonTitle
            style={{
              fontWeight: 600,
              color: "white",
              textAlign: "center",
            }}
          >
            All Courses
          </IonTitle>
        </IonToolbar>

        <style>
          {`
            .back-button {
              border: 1px solid rgba(255,255,255,0.3);
              color: white !important;
              border-radius: 8px;
              margin-left: 10px;
              transition: all 0.3s ease;
            }

            .back-button:hover {
              border: 1px solid white;
              background: rgba(255,255,255,0.1);
            }

            ion-title {
              width: 100%;
              justify-content: center;
            }
          `}
        </style>
      </IonHeader>

      <IonContent fullscreen>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Courses Grid */}
          <Grid container spacing={3}>
            {isLoading
              ? Array.from(new Array(coursesPerPage)).map((_, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <CourseSkeleton />
                </Grid>
              ))
              : currentCourses.map((course) => (
                <Grid item xs={12} sm={6} lg={4} key={course.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      overflow: "hidden",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                      },
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.04)",
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image="https://images.unsplash.com/photo-1542744094-24638eff58bb?auto=format&fit=crop&w=1200&q=80"
                        alt={course.name}
                        sx={{
                          transition: "transform 0.3s ease",
                          "&:hover": { transform: "scale(1.05)" },
                        }}
                      />
                      <Box sx={{ position: "absolute", top: 12, left: 12 }}>
                        <Chip
                          label={course.paymenttype || "Free"}
                          size="small"
                          sx={{
                            backgroundColor: course.paymenttype?.toLowerCase() === 'paid'
                              ? '#ff9800'
                              : 'rgba(255,255,255,0.95)',
                            color: course.paymenttype?.toLowerCase() === 'paid'
                              ? 'white'
                              : 'inherit',
                            fontWeight: 600,
                            backdropFilter: "blur(10px)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: "48px",
                        }}
                      >
                        {course.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          mt: -3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: "40px",
                        }}
                      >
                        {course.description || "No description available"}
                      </Typography>

                      <Box sx={{ mt: "auto" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "primary.main",
                            }}
                          >
                            {course.paymenttype?.toLowerCase() === 'paid'
                              ? `₦${course.amount}`
                              : 'Free'}
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<PlayIcon />}
                          onClick={() => handleEnrollClick(course)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.5,
                            background: course.paymenttype?.toLowerCase() === 'paid'
                              ? "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
                              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                            "&:hover": {
                              background: course.paymenttype?.toLowerCase() === 'paid'
                                ? "linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)"
                                : "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                              boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {course.paymenttype?.toLowerCase() === 'paid'
                            ? 'Subscribe Now'
                            : 'Enroll Now'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {/* Pagination */}
          {courseList.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 2,
                    fontWeight: 600,
                  },
                  "& .MuiPaginationItem-page.Mui-selected": {
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                  },
                }}
              />
            </Box>
          )}
        </Container>
      </IonContent>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Complete Your Subscription
            </Typography>
            {paymentStatus !== 'processing' && (
              <IconButton onClick={handleClosePaymentDialog} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {paymentStatus === 'idle' && (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Course Details
                </Typography>
                <Box sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedCourse?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {selectedCourse?.description}
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight={700}>
                    Amount: ₦{selectedCourse?.amount}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Enter Your Phone Number
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You will receive a payment prompt on your phone via Nomaba
              </Typography>

              <TextField
                fullWidth
                label="Phone Number"
                placeholder="08012345678"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                error={!!paymentError}
                helperText={paymentError || "Enter your Nigerian phone number"}
                sx={{ mt: 2 }}
                disabled={paymentStatus == 'processing'}
              />
            </>
          )}

          {paymentStatus === 'processing' && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom align="center">
                Processing Payment...
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Please check your phone for a payment prompt from Nomaba.
                <br />
                Do not close this window while processing.
              </Typography>
            </Box>
          )}

          {paymentStatus === 'success' && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
                Payment Successful!
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary">
                You have successfully subscribed to {selectedCourse?.name}
                <br />
                Redirecting to course...
              </Typography>
            </Box>
          )}

          {paymentStatus === 'failed' && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
                Payment Failed
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary" gutterBottom>
                {paymentError}
              </Typography>
              <Alert severity="error" sx={{ mt: 2 }}>
                Please try again or contact support if the issue persists.
              </Alert>
            </Box>
          )}
        </DialogContent>

        {paymentStatus === 'idle' && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleClosePaymentDialog}
              sx={{ mr: 2 }}
              disabled={paymentStatus == 'processing'}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={initiatePayment}
              disabled={paymentStatus == 'processing'}
              sx={{
                background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                color: 'white',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  background: "linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)",
                }
              }}
            >
              Pay ₦{selectedCourse?.amount}
            </Button>
          </DialogActions>
        )}

        {paymentStatus === 'failed' && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleClosePaymentDialog}
              sx={{ mr: 2 }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => setPaymentStatus('idle')}
              sx={{
                background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                color: 'white',
                fontWeight: 600,
                px: 4,
              }}
            >
              Try Again
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </IonPage>
  );
};

export default EnrollCourse;